
import React, { useState, useEffect } from 'react';
import { css } from '@emotion/css';
import Amplify, { Auth } from 'aws-amplify';
import Select from 'react-select';

const overlayStyle = css`
  position: fixed;
  top: 0;
  left: 0;
  width: 100vw;
  height: 100vh;
  background: rgba(0, 0, 0, 0.55);
  backdrop-filter: blur(6px);
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 9999;
  animation: fadeIn 0.2s ease-out forwards;

  @keyframes fadeIn {
    from { opacity: 0; }
    to { opacity: 1; }
  }
`;

const modalStyle = css`
  background: #fff;
  border-radius: 24px;
  width: 90%;
  max-width: 440px;
  padding: 36px 32px 32px;
  box-shadow: 0 20px 60px rgba(0, 0, 0, 0.18);
  position: relative;
  animation: popIn 0.3s cubic-bezier(0.175, 0.885, 0.32, 1.275) forwards;

  @keyframes popIn {
    from { opacity: 0; transform: scale(0.92); }
    to { opacity: 1; transform: scale(1); }
  }
`;

const closeBtnStyle = css`
  position: absolute;
  top: 16px;
  right: 20px;
  font-size: 20px;
  color: #bbb;
  cursor: pointer;
  border: none;
  background: none;
  line-height: 1;
  transition: color 0.2s;

  &:hover { color: #555; }
`;

const iconWrap = css`
  width: 56px;
  height: 56px;
  background: linear-gradient(135deg, #ff7e14, #e05500);
  border-radius: 16px;
  display: flex;
  align-items: center;
  justify-content: center;
  margin-bottom: 20px;
  font-size: 26px;
  box-shadow: 0 6px 20px rgba(241, 102, 2, 0.35);
`;

const titleStyle = css`
  font-family: 'Poppins', sans-serif;
  font-size: 22px;
  font-weight: 700;
  color: #1a1a1a;
  margin: 0 0 6px;
`;

const subtitleStyle = css`
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: #888;
  margin: 0 0 28px;
`;

const labelStyle = css`
  font-family: 'Poppins', sans-serif;
  font-size: 12px;
  font-weight: 700;
  color: #555;
  letter-spacing: 0.8px;
  text-transform: uppercase;
  display: block;
  margin-bottom: 8px;
`;

const inputStyle = css`
  width: 100%;
  height: 50px;
  border-radius: 12px;
  border: 1.5px solid #eaeaea;
  padding: 0 16px;
  font-family: 'Poppins', sans-serif;
  font-size: 14px;
  color: #333;
  background: #fafafa;
  transition: border-color 0.2s, box-shadow 0.2s;
  box-sizing: border-box;

  &:focus {
    outline: none;
    border-color: #f16602;
    box-shadow: 0 0 0 3px rgba(241, 102, 2, 0.1);
    background: #fff;
  }

  &::placeholder { color: #bbb; }
`;

const fieldWrap = css`
  margin-bottom: 20px;
`;

const errorStyle = css`
  background: #fff3f3;
  border: 1px solid #ffcccc;
  color: #cc0000;
  font-size: 13px;
  font-family: 'Poppins', sans-serif;
  border-radius: 10px;
  padding: 10px 14px;
  margin-bottom: 18px;
`;

const submitBtnStyle = css`
  width: 100%;
  height: 52px;
  background: linear-gradient(135deg, #ff7e14 0%, #e05500 100%);
  color: #fff;
  border: none;
  border-radius: 14px;
  font-family: 'Poppins', sans-serif;
  font-size: 15px;
  font-weight: 700;
  cursor: pointer;
  margin-top: 8px;
  transition: all 0.2s;
  box-shadow: 0 6px 20px rgba(241, 102, 2, 0.35);

  &:hover {
    transform: translateY(-2px);
    box-shadow: 0 10px 28px rgba(241, 102, 2, 0.45);
  }

  &:active { transform: translateY(0); }
`;

const selectStyles = {
  control: (base, state) => ({
    ...base,
    height: '50px',
    borderRadius: '12px',
    border: state.isFocused ? '1.5px solid #f16602' : '1.5px solid #eaeaea',
    boxShadow: state.isFocused ? '0 0 0 3px rgba(241,102,2,0.1)' : 'none',
    background: state.isFocused ? '#fff' : '#fafafa',
    fontFamily: 'Poppins, sans-serif',
    fontSize: '14px',
    cursor: 'pointer',
    '&:hover': { borderColor: '#f16602' },
  }),
  option: (base, state) => ({
    ...base,
    fontFamily: 'Poppins, sans-serif',
    fontSize: '14px',
    background: state.isSelected ? '#f16602' : state.isFocused ? 'rgba(241,102,2,0.08)' : '#fff',
    color: state.isSelected ? '#fff' : '#333',
    cursor: 'pointer',
  }),
  menu: (base) => ({
    ...base,
    borderRadius: '14px',
    boxShadow: '0 10px 40px rgba(0,0,0,0.12)',
    overflow: 'hidden',
  }),
  menuPortal: (base) => ({
    ...base,
    zIndex: 99999,
  }),
  placeholder: (base) => ({ ...base, color: '#bbb' }),
};

const categorytypes = ['Mathematics', 'General Knowledge', 'Chess', 'SpellBowl'];

function CreateBatchPopUp(props) {
  const [batchname, setBatchname] = useState('');
  const [category, setCategory] = useState('');
  const [status, setStatus] = useState('Create Batch');
  const [checks, setChecks] = useState('');

  useEffect(() => {
    document.title = 'Create Batch';
  }, []);

  const getToken = async () => {
    try {
      const user = await Auth.currentAuthenticatedUser();
      Auth.currentSession().then((res) => {
        const jwt = res.getAccessToken().getJwtToken();
        const useremail = Auth.user.attributes.email;
        const username = user.username.includes('Google_')
          ? user.attributes['email']
          : user.username;
        createBatch(batchname, username, useremail, jwt);
      });
    } catch (error) {
      Auth.signOut();
    }
  };

  const createBatch = async (batchname, username, useremail, jwt) => {
    if (!batchname || batchname === 'Batch Name') {
      setChecks('Please enter a valid batch name.');
      return;
    }
    if (!category) {
      setChecks('Please select a category.');
      return;
    }
    setChecks('');
    setStatus('Creating...');
    const id = Date.now();
    const requestOptions = {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Authorization: jwt },
      body: JSON.stringify({
        category,
        classowner: username,
        id,
        name: batchname,
        email: useremail,
        sendEmail: true,
        members: [],
      }),
    };
    await fetch('https://api.quizwizard.net/quiz/createbatch', requestOptions);
    props.selected();
  };

  return (
    <div className={overlayStyle} onClick={props.handleClose}>
      <div className={modalStyle} onClick={(e) => e.stopPropagation()}>
        <button className={closeBtnStyle} onClick={props.handleClose}>✕</button>

        <div className={iconWrap}>🎓</div>
        <h3 className={titleStyle}>Create New Batch</h3>
        <p className={subtitleStyle}>Set up a new student group to assign challenges and track progress.</p>

        {checks && <div className={errorStyle}>{checks}</div>}

        <div className={fieldWrap}>
          <label className={labelStyle}>Batch Name</label>
          <input
            className={inputStyle}
            name="batchname"
            type="text"
            spellCheck="false"
            autoComplete="off"
            value={batchname}
            onChange={(e) => setBatchname(e.target.value)}
            placeholder="e.g. Math Class 2025"
          />
        </div>

        <div className={fieldWrap}>
          <label className={labelStyle}>Category</label>
          <Select
            name="category"
            styles={selectStyles}
            classNamePrefix="select"
            value={category ? { label: category, value: category } : null}
            placeholder="Select a category…"
            onChange={(e) => setCategory(e.value)}
            options={categorytypes.map((t) => ({ label: t, value: t }))}
            menuPortalTarget={document.body}
            menuPosition="fixed"
          />
        </div>

        <button className={submitBtnStyle} onClick={getToken}>
          {status}
        </button>
      </div>
    </div>
  );
}

export default CreateBatchPopUp;
